import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPhone, FiMail, FiMenu, FiX, FiMapPin, FiClock } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import styles from './Navbar.module.css';

const WHATSAPP = 'https://wa.me/16726676878?text=Hi%20Murat%2C%20I%20would%20like%20to%20book%20an%20oil%20change%20service.';

const LANGUAGES = [
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'fr', label: 'FR', flag: '🇫🇷' },
  { code: 'tr', label: 'TR', flag: '🇹🇷' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const navLinks = [
    { label: t('nav.home'), href: '#home' },
    { label: t('nav.about'), href: '#about' },
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.booking'), href: '#booking' },
    { label: t('nav.contact'), href: '#contact' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('lang', code);
    setLangOpen(false);
  };

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  return (
    <>
      <div className={styles.topBar}>
        <div className={styles.topInner}>
          <div className={styles.topLeft}>
            <span className={styles.topItem}><FiMapPin size={12}/> {t('nav.mobileService')}</span>
            <span className={styles.topItem}><FiClock size={12}/> {t('nav.flexibleHours')}</span>
          </div>
          <div className={styles.topRight}>
            <a href="tel:+16726676878" className={styles.topItem}><FiPhone size={12}/> 672-667-6878</a>
            <a href="mailto:muratbulum@gmail.com" className={styles.topItem}><FiMail size={12}/> muratbulum@gmail.com</a>
          </div>
        </div>
      </div>

      <nav className={`${styles.nav} ${scrolled ? styles.scrolled : ''}`}>
        <div className={styles.inner}>
          <a href="#home" className={styles.brand} onClick={e => go(e, '#home')}>
            <div className={styles.brandIcon}>🔧</div>
            <div>
              <span className={styles.brandMain}>Mr.Bulum</span>
              <span className={styles.brandSub}>Mobile Oil Change</span>
            </div>
          </a>

          <ul className={styles.links}>
            {navLinks.map(l => (
              <li key={l.label}>
                <a href={l.href} className={styles.link} onClick={e => go(e, l.href)}>{l.label}</a>
              </li>
            ))}
          </ul>

          <div className={styles.actions}>
            {/* Language Switcher */}
            <div className={styles.langSwitcher}>
              <button className={styles.langBtn} onClick={() => setLangOpen(o => !o)}>
                <span>{currentLang.flag}</span>
                <span>{currentLang.label}</span>
                <span className={styles.langArrow}>▾</span>
              </button>
              <AnimatePresence>
                {langOpen && (
                  <motion.div
                    className={styles.langDropdown}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.15 }}
                  >
                    {LANGUAGES.map(lang => (
                      <button
                        key={lang.code}
                        className={`${styles.langOption} ${i18n.language === lang.code ? styles.langActive : ''}`}
                        onClick={() => changeLanguage(lang.code)}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.label}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a href={WHATSAPP} target="_blank" rel="noreferrer" className={styles.waCta}>
              <FaWhatsapp size={16}/> {t('nav.bookWhatsapp')}
            </a>
            <button className={styles.burger} onClick={() => setOpen(o => !o)} aria-label="menu">
              {open ? <FiX size={22}/> : <FiMenu size={22}/>}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div className={styles.mobile}
              initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.2 }}>
              {navLinks.map(l => (
                <a key={l.label} href={l.href} className={styles.mobileLink} onClick={e => go(e, l.href)}>{l.label}</a>
              ))}
              <a href={WHATSAPP} target="_blank" rel="noreferrer" className={styles.mobileWa}>
                <FaWhatsapp size={16}/> {t('nav.bookWhatsapp')}
              </a>
              {/* Mobile Language Switcher */}
              <div className={styles.mobileLangs}>
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    className={`${styles.mobileLangBtn} ${i18n.language === lang.code ? styles.mobileLangActive : ''}`}
                    onClick={() => { changeLanguage(lang.code); setOpen(false); }}
                  >
                    {lang.flag} {lang.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
}
